// @ts-check
import React, { useMemo } from 'react';
import PropTypes from 'prop-types';

import { Link, useNavigate } from 'react-router-dom';
import { LuArrowLeft, LuTriangleAlert } from 'react-icons/lu';

import { useProjectsListContext } from '@datahooks/ProjectsListContext';
import useBusinessViewWorkflowDetail from '@datahooks/useBusinessViewWorkflowDetail';

import LoadingIcon from '@components/navigation/LoadingIcon';
import { TestableAlert, TestableCard, TestableText } from '@components/general/TestableComponents';
import { ErrorCard, CARD_TYPES } from '@components/cards/ErrorCard';

import BusinessViewHeader from '../shared/BusinessViewHeader';
import BusinessViewProjectSummary from '../shared/BusinessViewProjectSummary';
import BusinessViewFlowSummaryCard from './BusinessViewFlowSummaryCard';
import BusinessViewFlowStatusCard from './BusinessViewFlowStatusCard';
import BusinessViewFlowImpactCard from './BusinessViewFlowImpactCard';
import BusinessViewLinkedTestsTable from './BusinessViewLinkedTestsTable';
import { buildProjectOptions, mapSummary } from '../shared/businessViewProjectMapper';
import { mapWorkflowDetail } from './businessViewDetailMapper';
import { buildOverviewRoute } from '../shared/businessViewConfig';

/**
 * Detail view for a single business flow: what it is, what it is costing, and the linked
 * tests behind that verdict.
 *
 * Both ids come from the route so the view is linkable - the project cannot be inferred
 * from the workflow. Passing `data` bypasses the fetch, for tests and previews.
 *
 * @param {Object} props
 * Both ids are optional because they come from useParams, which types every route param as
 * possibly absent. Without them the detail query stays disabled and the not-found body shows.
 *
 * @param {string} [props.projectId]
 * @param {string} [props.workflowId]
 * @param {import('../shared/businessView.types').BusinessViewWorkflowDetailResponse} [props.data]
 * @returns {JSX.Element}
 */
const BusinessViewFlowDetail = ({ projectId, workflowId, data }) => {
  const navigate = useNavigate();

  const {
    filteredFlatProjects = [],
    isLoading: projectsLoading,
  } = useProjectsListContext();

  const projectOptions = useMemo(() => buildProjectOptions(filteredFlatProjects), [filteredFlatProjects]);

  const selectedProject = useMemo(
    () => filteredFlatProjects.find((/** @type {any} */ project) => String(project.projectId) === String(projectId)),
    [filteredFlatProjects, projectId]
  );

  const { detail, notFound, isLoading: detailLoading, error } = useBusinessViewWorkflowDetail(
    data ? null : projectId,
    data ? null : workflowId
  );

  const view = useMemo(() => mapWorkflowDetail(data ?? detail), [data, detail]);

  const isLoading = projectsLoading || detailLoading;
  const hasFlow = !!(data ?? detail);

  /** The body below the back link: error, loading, not found or the flow itself. */
  let body;

  if (error) {
    body = (
      <TestableCard dataTestId="bv-flow-error-card" className="mt-5 w-full p-4 text-center text-blue-500">
        <ErrorCard cardType={CARD_TYPES.ERROR} />
        <TestableText dataTestId="bv-flow-error-title" className="mt-10">An error occurred while fetching this business flow.</TestableText>
        <TestableText dataTestId="bv-flow-error-detail" className="mt-5">Please try again later.</TestableText>
      </TestableCard>
    );
  } else if (isLoading) {
    body = (
      <div data-testid="bv-flow-loading" className="flex w-full justify-center py-16">
        <LoadingIcon size={100} showText />
      </div>
    );
  } else if (notFound || !hasFlow) {
    body = (
      <div data-testid="bv-flow-not-found" className="mt-5 w-full p-4 text-center text-blue-500">
        <ErrorCard cardType={CARD_TYPES.EMPTY} />
        <TestableText dataTestId="bv-flow-not-found-title" variant="lead" className="mt-10">
          This business flow could not be found.
        </TestableText>
        {/*
          The API answers 404 both for a workflow that does not exist and for one that
          belongs to another project, so this must not claim which.
        */}
        <TestableText dataTestId="bv-flow-not-found-detail" className="mt-5">
          It may have been removed, or it may belong to a different project. Go back to the flows
          list and pick it from there.
        </TestableText>
      </div>
    );
  } else {
    body = (
      <>

        {/*
          A partial calculation still has a status and counts, but at least one linked test was indeterminate 
					— so the status is stated as less than certain rather than presented as a clean result.
        */}
        {view.isPartial && (
          <TestableAlert
            dataTestId="bv-flow-partial-notice"
						variant="filled"
            className="text-orange-500 bg-orange-500/20 dark:text-gray-300 border-orange-500 border"
          >
						<TestableText dataTestId="bv-flow-partial-notice-text" as="span" className="text-orange-500">
							<LuTriangleAlert size="24" className="inline mr-2 text-orange-500 font-bold" aria-hidden="true" />
            	This status is based on partial results because some linked tests could not be determined:
            </TestableText>
						 <ul className="mt-2 ml-12 list-inside list-disc">
              <li>{view.workflow.calculationMessage}</li>
						</ul>
            
          </TestableAlert>
        )}

        <BusinessViewFlowSummaryCard
          workflow={view.workflow}
          coverageMix={view.coverageMix}
          dailyCost={view.dailyCost}
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <div className="lg:col-span-1">
            <BusinessViewFlowStatusCard
              workflow={view.workflow}
              breakdown={view.breakdown}
              testCount={view.tests.length}
              isUnavailable={view.isUnavailable}
            />
          </div>
          <div className="lg:col-span-2">
            <BusinessViewFlowImpactCard
              workflow={view.workflow}
              nonPassingCount={view.nonPassingCount}
              dailyCost={view.dailyCost}
              hasCurrentRisk={view.hasCurrentRisk}
              exposure7Day={view.exposure7Day}
              exposure30Day={view.exposure30Day}
            />
          </div>
        </div>

        <BusinessViewLinkedTestsTable
          tests={view.tests}
          passingCount={view.passingCount}
          nonPassingCount={view.nonPassingCount}
        />
      </>
    );
  }

  return (
    <div data-testid="business-view-flow-detail" className="mx-auto flex w-full flex-col gap-6 py-6">
      <BusinessViewHeader
        projects={projectOptions}
        selectedProjectId={String(projectId ?? '')}
        /* Another project has its own flows, so switching lands on that project's list. */
        onProjectChange={(id) => navigate(buildOverviewRoute(id))}
      />

			
      <BusinessViewProjectSummary summary={mapSummary(selectedProject)} />

      <Link
        data-testid="bv-flow-back-link"
        to={buildOverviewRoute(projectId)}
        className="flex w-fit items-center gap-2 font-semibold text-blue-500 hover:underline"
      >
        <LuArrowLeft className="h-5 w-5" aria-hidden="true" />
        Back to all flows
      </Link>

      {body}
    </div>
  );
};

BusinessViewFlowDetail.propTypes = {
  projectId: PropTypes.string,
  workflowId: PropTypes.string,
  data: PropTypes.object,
};

export default BusinessViewFlowDetail;
